import { FC, useState } from 'react'
import { PerModal } from '../../components/custom_components/Modal'
import { Col, Select } from 'antd'
import { EmployeeWithRole, RoleOfUser } from '../../types'
import { useTranslation } from 'react-i18next'
import PerText from '../../components/custom_components/Text'
import PerFlexRow from '../../components/custom_components/FlexRow'
import { roleColor } from './utils'
import { PerTag } from '../../components/custom_components/Tag'
const { Option } = Select

interface GlobalAdminRemoveRoleModalProps {
  open: boolean
  employee?: EmployeeWithRole
  selectedRoleOfUser?: string
  error?: string
  onCancel: () => void
  deleteRole: () => Promise<void>
  setSelectedRoleOfUser: (value: string) => void
}

export const GlobalAdminRemoveRoleModal: FC<GlobalAdminRemoveRoleModalProps> = ({
  open,
  employee,
  selectedRoleOfUser,
  error,
  onCancel,
  deleteRole,
  setSelectedRoleOfUser,
}) => {
  const { t } = useTranslation()
  const [isSubmitting, toggleSubmitting] = useState<boolean>(false)

  const employeeHRRoles = (employee: EmployeeWithRole): RoleOfUser[] => {
    return employee.hrRoleOfUser
      .filter((employeeRole: RoleOfUser) => employeeRole.role.name.includes('HR_'))
      .filter((employeeRole: RoleOfUser) => {
        const roleParts = employeeRole.role.name.split('_')
        return roleParts[1] === 'ADMIN' || roleParts[1] === 'READONLY'
      })
  }
  const employeeRestrictedRoles = (employee: EmployeeWithRole): RoleOfUser[] => {
    return employee.hrRoleOfUser
      .filter((employeeRole: RoleOfUser) => employeeRole.role.name.includes('HR_'))
      .filter((employeeRole: RoleOfUser) => {
        const roleParts = employeeRole.role.name.split('_')
        return roleParts[1] === 'RESTRICTED'
      })
  }

  const onDeleteRole = async () => {
    toggleSubmitting(true)
    await deleteRole()
    toggleSubmitting(false)
  }

  return (
    <PerModal
      title={{ heading: t('globalAdminView.modal.removeRoleTitle') }}
      size="large"
      visible={open}
      onCancel={onCancel}
      footer={{
        cancelBtn: {
          onClick: onCancel,
        },
        customBtns: [
          {
            text: t('global.button.delete'),
            onClick: async () => {
              await onDeleteRole()
            },
            color: 'error',
            icon: 'delete_fill',
            disabled: !selectedRoleOfUser,
            isProcessing: { active: isSubmitting },
            tooltip: selectedRoleOfUser ? t('global.button.delete') : t('globalAdminView.modal.tooltipRoleMissing'),
          },
        ],
      }}
    >
      <>
        <PerFlexRow direction="row" mb="s">
          <Col span={4}>
            <PerFlexRow align="middle" className="fullHeight">
              <PerText color="secondary">{t('globalAdminView.modal.employee.label')}</PerText>
            </PerFlexRow>
          </Col>
          <Col span={20}>
            <PerText>
              {employee && employee.firstName} {employee && employee.lastName}
            </PerText>
          </Col>
        </PerFlexRow>

        <PerFlexRow direction="row" mb="s">
          <Col span={4}>
            <PerFlexRow align="middle" className="fullHeight">
              <PerText color="secondary">{t('globalAdminView.modal.role.label')}</PerText>
            </PerFlexRow>
          </Col>
          <Col span={20}>
            {employee && (employee.hrRoleOfUser.length > 0 || employee.hasDownloaderRole) && (
              <Select
                placeholder={t('globalAdminView.modal.role.placeholder')}
                value={selectedRoleOfUser}
                onChange={(value: string) => {
                  setSelectedRoleOfUser(value)
                }}
                className="fullWidth"
                disabled={!employee}
              >
                {employee.hasDownloaderRole && (
                  <Option key={`role-downloader`} value={'role-downloader'}>
                    <PerText>{t('globalAdminView.table.downloader.heading')}</PerText>
                  </Option>
                )}
                {employee.hrRoleOfUser.length > 0 &&
                  employeeHRRoles(employee).map((roleOfUser: RoleOfUser, index: number) => {
                    const roleParts = roleOfUser.role.name.split('_')
                    const restrictedRoles = employeeRestrictedRoles(employee)
                    return (
                      <Option key={`role-${index}`} value={roleOfUser.id}>
                        <PerFlexRow align="middle">
                          <PerText mr={'xs'}>{t(`global.country.${roleParts[2]}`)}</PerText>
                          <PerFlexRow align="middle">
                            <PerTag color={roleColor(roleOfUser.role.name)} key={`${employee.id}_${roleOfUser.id}`}>
                              {roleParts[1]}
                            </PerTag>
                          </PerFlexRow>
                          {restrictedRoles.length > 0 && restrictedRoles.map((r) => r.role.name.split('_')[2]).includes(roleParts[2]) && (
                            <PerText fontSize="xs" color="secondary" fontWeight="normal" ml="xxxs">
                              {t('globalAdminView.table.assignedTo.hrExcluded')}
                            </PerText>
                          )}
                        </PerFlexRow>
                      </Option>
                    )
                  })}
              </Select>
            )}
          </Col>
        </PerFlexRow>
        {error && (
          <PerText color="error" fontSize="m" mt="m">
            {error}
          </PerText>
        )}
      </>
    </PerModal>
  )
}
